import { createEntityAdapter, createSelector, createSlice, EntityState, PayloadAction } from '@reduxjs/toolkit';
import {
  getSortComparer,
  OperationCompanyOrderType,
  OperationCompanyOrderTypeOption,
  OperationCompanyOrderTypeOptionDefault,
} from '../../..';
import { FileResult } from '../../api/models/api-shared.models';
import { BusinessUnitLink } from '../../api/models/business-unit.models';
import { UpdateIsCriticalItemResultDetail } from '../../api/models/customer-product.models';
import { Customer } from '../../api/models/customer.models';
import { UserCustomer } from '../../api/models/site.models';
import { RootState } from '../store';

const orderTypeAdapter = createEntityAdapter<OperationCompanyOrderType>({
  selectId: (data: OperationCompanyOrderType) => data.OperationCompanyOrderTypeId,
});

// State

interface CustomerState {
  selectedCustomer: UserCustomer | undefined;
  customerBillto?: Customer;
  deliveryDates: string[];
  updateIsCriticalItemResultDetail?: UpdateIsCriticalItemResultDetail;
  businessUnitLinks: BusinessUnitLink[];
  operationCompanyOrderTypes: EntityState<OperationCompanyOrderType>;
  HasSpecialOrderReport?: boolean;
  specialOrderFile?: FileResult;
  specialOrderFileLoading?: boolean;
}

const initialState: CustomerState = {
  selectedCustomer: undefined,
  customerBillto: undefined,
  deliveryDates: [],
  updateIsCriticalItemResultDetail: undefined,
  businessUnitLinks: [],
  operationCompanyOrderTypes: orderTypeAdapter.getInitialState(),
  HasSpecialOrderReport: false,
  specialOrderFile: undefined,
  specialOrderFileLoading: false,
};

// Reducers
export const customerSlice = createSlice({
  name: 'customer',
  initialState: initialState,
  reducers: {
    setSelectedCustomer: (state: CustomerState, action: PayloadAction<UserCustomer>) => {
      state.selectedCustomer = action.payload;
    },
    setCustomerBillto: (state: CustomerState, action: PayloadAction<Customer>) => {
      state.customerBillto = action.payload;
    },
    setCustomerDeliveryDates: (state: CustomerState, action: PayloadAction<string[]>) => {
      state.deliveryDates = action.payload;
    },
    setIsCriticalItem: (state: CustomerState, action: PayloadAction<UpdateIsCriticalItemResultDetail>) => {
      state.updateIsCriticalItemResultDetail = action.payload;
    },
    setBusinessUnitLinks: (state: CustomerState, action: PayloadAction<BusinessUnitLink[]>) => {
      state.businessUnitLinks = action.payload;
    },
    setOperationCompanyOrderTypes: (state: CustomerState, action: PayloadAction<OperationCompanyOrderType[]>) => {
      orderTypeAdapter.setAll(state.operationCompanyOrderTypes, action.payload);
    },
    setHasSpecialOrderReport: (state: CustomerState, action: PayloadAction<boolean>) => {
      state.HasSpecialOrderReport = action.payload;
    },
    setSpecialOrderFile: (state: CustomerState, action: PayloadAction<FileResult | undefined>) => {
      state.specialOrderFile = action.payload;
    },
    setSpecialOrderFileLoading: (state: CustomerState, action: PayloadAction<boolean>) => {
      state.specialOrderFileLoading = action.payload;
    },
  },
});

const getDeliveryDates = (s: RootState) => s.customer.deliveryDates;

export const selectNextDeliveryDate = createSelector([getDeliveryDates], (dates): string | undefined => {
  const tomorrow = new Date();
  tomorrow.setDate(tomorrow.getDate() + 1);
  return dates?.[0] ?? tomorrow.toISOString();
});

export const { selectAll: selectAllCustomerOrderTypes, selectById: selectCustomerOrderTypeById } =
  orderTypeAdapter.getSelectors<EntityState<OperationCompanyOrderType>>(
    (state: EntityState<OperationCompanyOrderType>) => state
  );

export const selectAllCustomerOrderTypeOptions = createSelector([selectAllCustomerOrderTypes], (orderTypes) => {
  const options: OperationCompanyOrderTypeOption[] = orderTypes
    .map((p) => ({
      OperationCompanyOrderTypeId: p.OperationCompanyOrderTypeId,
      OrderTypeName: p.OrderTypeName,
    }))
    .sort(getSortComparer((x) => x.OrderTypeName));

  return [OperationCompanyOrderTypeOptionDefault, ...options];
});

export const selectCustomerOrderTypeOptionsById = createSelector([selectCustomerOrderTypeById], (orderTypeById) => {
  return orderTypeById || OperationCompanyOrderTypeOptionDefault;
});
